import { Import, Settings, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else {
    currentPath = false;
  }

  return (
    <nav className="grow">
      <header
        style={{
          background: "var(--nd-surface)",
          borderBottom: "1px solid var(--nd-border)",
        }}
      >
        <div className="px-6">
          <div className="flex justify-between items-center h-12">
            <Link
              to="/"
              className="flex items-center gap-2 no-underline shrink-0"
            >
              <img
                className="[.light_&]:hidden h-5"
                src={darkModeLogo}
                alt={title}
              />
              <img
                className="[.dark_&]:hidden h-5"
                src={lightModeLogo}
                alt={title}
              />
            </Link>

            <div className="flex items-center">
              <NavigationTab
                label={translate("ra.page.dashboard")}
                to="/"
                isActive={currentPath === "/"}
              />
              <NavigationTab
                label={translate("resources.contacts.name", {
                  smart_count: 2,
                })}
                to="/contacts"
                isActive={currentPath === "/contacts"}
              />
              <NavigationTab
                label={translate("resources.companies.name", {
                  smart_count: 2,
                })}
                to="/companies"
                isActive={currentPath === "/companies"}
              />
              <NavigationTab
                label={translate("crm.menu.deals", { _: "Pipeline" })}
                to="/deals"
                isActive={currentPath === "/deals"}
              />
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <ThemeModeToggle />
              <RefreshButton />
              <UserMenu>
                <ProfileMenu />
                <CanAccess resource="sales" action="list">
                  <UsersMenu />
                </CanAccess>
                <CanAccess resource="configuration" action="edit">
                  <SettingsMenu />
                </CanAccess>
                <ImportFromJsonMenuItem />
              </UserMenu>
            </div>
          </div>
        </div>
      </header>
    </nav>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    aria-current={isActive ? "page" : undefined}
    className="px-4 h-12 flex items-center font-nd-mono text-[10px] tracking-[0.08em] uppercase transition-colors no-underline"
    style={{
      color: isActive ? "var(--nd-text-display)" : "var(--nd-text-secondary)",
      borderBottom: isActive
        ? "1px solid var(--nd-text-display)"
        : "1px solid transparent",
    }}
  >
    {label}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
